import { defaultCoordinates, defaultRectangleCoords } from '@/context/default';
import {
  HomeTypeEnum,
  Icons,
  OwnershipType,
  Weights,
  ColumnEnum,
} from '@/enums';
import strings, { homeTypes, ownershipTypes } from '@/language/Swedish';
import {
  AdResponseType,
  SoldSearchCriteriaType,
  SaleRefResponseType,
  AdsSearchParamsCriteriaType,
  AdsMarketSearchParamsCriteriaType,
  IndexValueType
} from '@/types/apiTypes';
import {
  CoordinatesType,
  AdsTableType,
  MarkerObjectType,
  SalesRefTableType,
  AdsRefDataType,
  SoldRefDataType,
  MarketRefDataType,
  SoldWeightDataType,
  AdsWeightDataType,
  AdsFilterDataType,
  MarketFilterDataType,
  squareSideType,
} from '@/types/dataTypes';
import proj4 from 'proj4';
import * as Sentry from '@sentry/react';

const getDistance = (zoomLevel: number) => {
  const baseDistance = 400;
  return baseDistance * Math.pow(2, 15 - zoomLevel);
};

const calculateBounds = (center: CoordinatesType | null, distance: number) => {
  if (!center || !center.lat || !center.lng) {
    return defaultRectangleCoords;
  }

  const earthRadius = 6378137;
  const latDiff = (distance / earthRadius) * (180 / Math.PI);
  const lngDiff =
    (distance / (earthRadius * Math.cos((Math.PI * center.lat) / 180))) *
    (180 / Math.PI);

  return {
    north: center.lat + latDiff,
    south: center.lat - latDiff,
    east: center.lng + lngDiff,
    west: center.lng - lngDiff,
  };
};

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWYZ';

const getMarkerLabel = (index: number) => {
  const label =
    index <= 8 ? index + 1 : index < alphabet.length ? alphabet[index - 9] : '';
  return label;
};

const getAboutListItems = (refData: SoldRefDataType) => [
  {
    title: strings.table.columns.rooms,
    icon: Icons.bed,
    value: refData.rooms_reference,
  },
  {
    title: `${strings.table.columns.livingArea} m\u00B2`,
    icon: Icons.area,
    value: refData.living_area_reference
      ? `${refData.living_area_reference} m\u00B2`
      : '',
  },
  {
    title: strings.table.columns.constructionYear,
    icon: Icons.crane,
    value: refData.construction_year_reference,
  },
  {
    title: strings.filter.floor,
    icon: Icons.apartment,
    value: refData.floor_reference,
  },
  {
    title: strings.filter.monthly_fee,
    icon: Icons.dollar,
    value: refData.monthly_fee_reference,
  },
];

const getSalesReferenceData = (
  data: SaleRefResponseType[]
): SalesRefTableType[] => {
  return data.map((item): SalesRefTableType => {
    const tableItems = {
      id: item.id,
      street: `${item.street} ${item.number || ''}${item.letter || ''}`,
      adress: `${item.street} ${item.number || ''}${item.letter || ''}`,
      rooms: item.rooms,
      livingArea: item.living_area,
      floorFloors: `${item.floor ?? '--'}/${item.floors ?? '--'}`,
      floor: item.floor,
      floors: item.floors,
      constructionYear: item.construction_year,
      homeType: item.home_type,
      ancillaryArea: item.ancillary_area,
      soldPrice: item.sold_price,
      monthlyFee: item.monthly_fee,
      recountedPrice: item.recounted_price,
      contractDate: item.contract_date,
      relevance: item.relevance,
      ownSale: item.own_sale,
      coordinates: { lat: item.wgs84lat, lng: item.wgs84lon },
      adPrice: item.ad_price,
      ad_delete_date: item.ad_delete_date,
      priceTaxationRatio: item.price_taxation_ratio?.toString().replace(".", ","),
      soldPriceSqm: item.price_sqm,
      recountedPriceSqm: item.recounted_price_sqm,
      taxationValue: item.taxation_value,
      taxationYear: item.taxation_year,
      valuationYear: item.construction_value_year,
      monthlyFeeSqm: item.monthly_fee_sqm,
      source: item.source,
      firm: item.firm,
      adRegisteredDate: item.ad_registered_date,
      adAdvertisingDays: item.ad_advertising_days,
      adPriceSqm: item.ad_price_sqm,
      prospectUrl: item.prospect_url,
      propertyUnitDesignation: item.property_unit_designation,
      plotArea: item.plot_area,
      ownershipType: item.ownership_type,
      // API may serialize a missing owner_name as the literal string "NaN"
      ownerName: item.owner_name === 'NaN' ? '' : item.owner_name,
      ownerId: item.owner_id,
      allabrfUrl: item.allabrf_url,
      allabolagUrl: item.allabolag_url,
      shoreDistanceType: item.shore_distance_type,
      waterSewerageType: item.water_sewerage_type,
      alterationYear: item.alteration_year,
      standardPoints: item.standard_points,
      leaseholdFee: item.leasehold_fee,
      leaseholdPeriod: item.leasehold_period,
      subdivisionType: item.subdivision_type,
      housingAssociationReportYear: item.housing_association_report_year,
      balcony: item.balcony,
      elevator: item.elevator,
    };

    return tableItems;
  });
};

const getAdsData = (data: AdResponseType[]): AdsTableType[] => {
  return data.map((item): AdsTableType => {
    const tableItems = {
      id: item.id, // TODO: check
      street: `${item.street} ${item.number}${item.letter || ''}`,
      adress: `${item.street} ${item.number || ''}${item.letter || ''}`,
      rooms: item.rooms,
      livingArea: item.living_area,
      floorFloors: `${item.floor ?? '--'}/${item.floors ?? '--'}`,
      floor: item.floor,
      floors: item.floors,
      constructionYear: item.construction_year,
      homeType: item.home_type,
      ownershipType: item.ownership_type,
      ancillaryArea: item.ancillary_area,
      registeredDate: item.registered_date,
      advertisingDays: item.advertising_days,
      plotArea: item.plot_area,
      ownerId: item.owner_id,
      monthlyFee: item.monthly_fee,
      price: item.price,
      priceSqm: item.price_sqm,
      firm: item.firm,
      // API may serialize a missing owner_name as the literal string "NaN"
      ownerName: item.owner_name === 'NaN' ? '' : item.owner_name,
      active: item.active,
      pdf: item.url,
      coordinates: getWGS84fromRT90(item.rt90x, item.rt90y),
    };
    return tableItems;
  });
};

const modifyValueBasedOnKeyForTable = (
  value: string,
  key: string
): string | number => {

  const priceKeys = [
    ColumnEnum.soldPrice,
    ColumnEnum.price,
    ColumnEnum.priceSqm,
    ColumnEnum.soldPriceSqm,
    ColumnEnum.monthlyFee,
    ColumnEnum.soldPriceSqm,
    ColumnEnum.recountedPrice,
    ColumnEnum.recountedPriceSqm,
    ColumnEnum.taxasionValue,
    ColumnEnum.ownSale,
    ColumnEnum.monthlyFeeSqm,
    ColumnEnum.adPrice,
    ColumnEnum.adPriceSqm,
    ColumnEnum.plotArea
  ] as const;

  const numbersThatAreNotNumbers = [ // priceTaxationRatio is now string, but keeping this for future use
    ColumnEnum.priceTaxationRatio
  ]

  if (key == ColumnEnum.homeType) {
    return homeTypes[value as keyof typeof homeTypes];
  } else if (key == ColumnEnum.ownershipType) {
    return ownershipTypes[value];
  } else if (key == ColumnEnum.plotArea) {
    return formatNumber(parseInt(value));
  } else if (priceKeys.includes(key as keyof typeof ColumnEnum | any)) {
    return formatPrice(parseInt(value));
  } else if (typeof value == "number" && !numbersThatAreNotNumbers.includes(key as keyof typeof ColumnEnum | any)) {
    return parseInt(value);
  } 
  else {
    return value;
  }
};

const modifyValueBasedOnKeyForExcel = (
  value: string,
  key: string
): string | number => {

  const priceKeys = [
    ColumnEnum.soldPrice,
    ColumnEnum.price,
    ColumnEnum.priceSqm,
    ColumnEnum.soldPriceSqm,
    ColumnEnum.monthlyFee,
    ColumnEnum.recountedPrice,
    ColumnEnum.recountedPriceSqm,
    ColumnEnum.taxasionValue,
    ColumnEnum.ownSale,
    ColumnEnum.monthlyFeeSqm,
    ColumnEnum.adPrice,
    ColumnEnum.adPriceSqm
  ] as const;

  const stringFieldsThatShouldBeNumbers = [
    ColumnEnum.priceTaxationRatio,
    ColumnEnum.waterSewerageType
  ] as const;

  const stringFieldsThatShouldStayStrings = [
    ColumnEnum.ownershipType
  ] as const;

  if (key == ColumnEnum.balcony || key == ColumnEnum.elevator) {
    return value === 1 || value === '1' ? 'Ja' : '';
  } else if (key == ColumnEnum.homeType) {
    return homeTypes[value as keyof typeof homeTypes];
  } else if (key == ColumnEnum.ownershipType) {
    return ownershipTypes[value];
  } else if (stringFieldsThatShouldStayStrings.includes(key as keyof typeof ColumnEnum | any)) {
    return value;
  } else if (priceKeys.includes(key as keyof typeof ColumnEnum | any)) {
    // Return numeric value for Excel instead of formatted string
    const numValue = parseInt(value);
    return isNaN(numValue) ? value : numValue;
  } else if (stringFieldsThatShouldBeNumbers.includes(key as keyof typeof ColumnEnum | any)) {
    // Convert string fields that should be numbers to actual numbers
    const numValue = parseFloat(value);
    return isNaN(numValue) ? value : numValue;
  } else if (typeof value == "number") {
    return value;
  } else {
    return value;
  }
};

const getMapMarkerDataFromSold = (
  data: SaleRefResponseType[]
): MarkerObjectType[] => {
  return data.map((item, index) => ({
    id: item.id,
    index,
    location: { lat: item.wgs84lat, lng: item.wgs84lon },
    street: `${item.street} ${item.number}${item.letter}`,
    livingArea: item.living_area,
    price: item.sold_price,
    date: item.contract_date,
  }));
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null; // Return null if the cookie is not found
};

const isLoggedIn = () => {
  const authTokenCookie = getCookie('authToken');
  if (authTokenCookie == 'undefined') {
    return false;
  } else {
    return !!authTokenCookie;
  }
};

const getMapMarkerDataFromAds = (
  data: AdResponseType[]
): MarkerObjectType[] => {
  return data.map((item, index) => ({
    id: item.id,
    index,
    location: getWGS84fromRT90(item.rt90x, item.rt90y),
    street: `${item.street} ${item.number}${item.letter}`,
    livingArea: item.living_area,
    price: item.price,
    date: item.registered_date,
  }));
};

const getMapMarkerDataFromSoldTable = (
  data: SalesRefTableType[]
): MarkerObjectType[] => {
  return data.map(
    (
      { id, street, livingArea, soldPrice, contractDate, coordinates },
      index
    ) => ({
      id,
      index,
      location: coordinates,
      street,
      livingArea,
      price: soldPrice,
      date: contractDate,
    })
  );
};

const getMapMarkerDataFromAdsTable = (
  data: AdsTableType[]
): MarkerObjectType[] => {
  return data.map(
    (
      { id, street, livingArea, price, registeredDate, coordinates },
      index
    ) => ({
      id,
      index,
      location: coordinates,
      street,
      livingArea,
      price,
      date: registeredDate,
    })
  );
};

const getWGS84fromRT90 = (rt90x: number, rt90y: number): CoordinatesType => {
  proj4.defs([
    [
      'RT90',
      '+proj=tmerc +lat_0=0 +lon_0=15.8082777777778 +k=1 +x_0=1500000 +y_0=0 +ellps=bessel +towgs84=414.1,41.3,603.1,-0.855,2.141,-7.023,0 +units=m +no_defs',
    ],
    ['WGS84', '+proj=longlat +datum=WGS84 +no_defs'],
  ]);

  const wgs84Coordinates = proj4('RT90', 'WGS84', !isNaN(rt90y) && !isNaN(rt90x) ? [rt90y, rt90x] : [0, 0]);

  const coordinates = {
    lat: wgs84Coordinates[1],
    lng: wgs84Coordinates[0],
  };

  return coordinates;
};

function getRelativeLuminance(hexColor: string): nunmber {
  // Convert hex color to RGB
  let r, g, b;
  if (hexColor.length === 4) {
    r = parseInt(hexColor[1] + hexColor[1], 16) / 255;
    g = parseInt(hexColor[2] + hexColor[2], 16) / 255;
    b = parseInt(hexColor[3] + hexColor[3], 16) / 255;
  } else if (hexColor.length === 7) {
    r = parseInt(hexColor.slice(1, 3), 16) / 255;
    g = parseInt(hexColor.slice(3, 5), 16) / 255;
    b = parseInt(hexColor.slice(5, 7), 16) / 255;
  } else {
    throw new Error('Invalid color format');
  }

  // Apply gamma correction
  r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  // Calculate relative luminance
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function isContrastWCAG(hexColor1: string, hexColor2: string): boolean {


  // Get the relative luminance of both colors
  const lum1 = getRelativeLuminance(hexColor1);
  const lum2 = getRelativeLuminance(hexColor2);

  // Calculate the contrast ratio
  const contrastRatio = (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
  return contrastRatio > 4.5;
}

const getRT90fromWGS84 = (
  lat: number,
  lng: number
): { rt90x: number; rt90y: number } => {
  proj4.defs([
    [
      'RT90',
      '+proj=tmerc +lat_0=0 +lon_0=15.8082777777778 +k=1 +x_0=1500000 +y_0=0 +ellps=bessel +towgs84=414.1,41.3,603.1,-0.855,2.141,-7.023,0 +units=m +no_defs',
    ],
    ['WGS84', '+proj=longlat +datum=WGS84 +no_defs'],
  ]);
  const rt90Coordinates = proj4('WGS84', 'RT90', !isNaN(lng) && !isNaN(lat) ? [lng, lat] : [0, 0]);
  const coordinates = {
    rt90x: rt90Coordinates[1],
    rt90y: rt90Coordinates[0],
  };
  return coordinates;
};

const formatNumber = (numberToFormat: number) => {
  return new Intl.NumberFormat('sv-SE', {
  }).format(numberToFormat);
};

const formatPrice = (price: number) => {
  const newPrice = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(
    price
  );
  return isNaN(parseInt(newPrice)) ? price : newPrice
};



const formatDateToString = (date: Date): string | null => {
  if (Number.isNaN(date.getDay())) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getRefApiParams = (
  data: SoldRefDataType | AdsRefDataType | MarketRefDataType
) => {
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([key, value]) => value !== null && value !== undefined
    )
  );

  return filteredData;
};

const getWeightApiParams = (data: SoldWeightDataType | AdsWeightDataType) => {
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([key, value]) => value !== null && value !== undefined && value !== -1
    )
  );

  return filteredData;
};

const getMarketsFilterParamsFromApiData = (
  marketSearchCritera: AdsMarketSearchParamsCriteriaType
) => {
  if (marketSearchCritera === null) return null;

  const marketFilterValues = {
    registered_date_min: marketSearchCritera?.registered_date_min
      ? new Date(marketSearchCritera?.registered_date_min)
      : null,
    registered_date_max: marketSearchCritera?.registered_date_max
      ? new Date(marketSearchCritera?.registered_date_max)
      : null,
    living_area_min: marketSearchCritera.living_area_min ?? null,
    living_area_max: marketSearchCritera.living_area_max ?? null,
    rooms_max: marketSearchCritera.rooms_max ?? null,
    rooms_min: marketSearchCritera.rooms_min ?? null,
    price_max:
      marketSearchCritera.price_max && marketSearchCritera?.price_max / 1000000,
    price_min:
      marketSearchCritera.price_min && marketSearchCritera?.price_min / 1000000,
    construction_year_max: marketSearchCritera.construction_year_max ?? null,
    construction_year_min: marketSearchCritera.construction_year_min ?? null,
    monthly_fee_max: marketSearchCritera.monthly_fee_max ?? null,
    monthly_fee_min: marketSearchCritera.monthly_fee_min ?? null,

    home_type: marketSearchCritera?.home_type?.length
      ? marketSearchCritera.home_type
      : null,
    ownership_type: marketSearchCritera?.ownership_type?.length
      ? marketSearchCritera.ownership_type
      : null,
  };

  const cleanMarketFiltedValues = removeNullApiParams(
    marketFilterValues
  ) as Partial<MarketFilterDataType>;

  return { marketFilterValues: cleanMarketFiltedValues };
};

function getTodayDateISO(): string {
  let today = new Date()
  const offset = today.getTimezoneOffset()
  today = new Date(today.getTime() - (offset * 60 * 1000))
  return convertDateToISO(today);
}

function convertDateToISO(date: Date): string {
  return date.toISOString().split('T')[0]
}

function convertDateStringToISO(date: string): string {
  if (!date) {
    return date;
  }
  return date.split('T')[0]
}

function filterLineChartDataBasedOnDateInterval(data: IndexValueType[], priceDevelopmentSelectedFrom: string, priceDevelopmentSelectedTo: string): IndexValueType[] {
  return data.filter((item) => {
    return new Date(item.average_date) >= new Date(priceDevelopmentSelectedFrom) && new Date(item.average_date) <= new Date(priceDevelopmentSelectedTo)
  })
}

const getAdsFilterParamsFromApiData = (
  adsSearchCritera: AdsSearchParamsCriteriaType
) => {
  const adsRefValues = {
    price_reference: adsSearchCritera?.price_reference ?? null,
    living_area_reference: adsSearchCritera?.living_area_reference ?? null,
    floor_reference: adsSearchCritera?.floor_reference ?? null,
    floors_reference: adsSearchCritera?.floors_reference ?? null,
    rooms_reference: adsSearchCritera?.rooms_reference ?? null,
    ancillary_area_reference:
      adsSearchCritera?.ancillary_area_reference ?? null,
    construction_year_reference:
      adsSearchCritera?.construction_year_reference ?? null,
    monthly_fee_reference: adsSearchCritera?.monthly_fee_reference ?? null,
  };

  const adsWeightValues = {
    construction_year_weight:
      (adsSearchCritera?.construction_year_weight as Weights) ?? Weights.none,
    distance_weight:
      (adsSearchCritera?.distance_weight as Weights) ?? Weights.none,
    floor_weight: (adsSearchCritera?.floor_weight as Weights) ?? Weights.none,
    floors_weight: (adsSearchCritera?.floors_weight as Weights) ?? Weights.none,
    living_area_weight:
      (adsSearchCritera?.living_area_weight as Weights) ?? Weights.none,
    monthly_fee_weight:
      (adsSearchCritera?.monthly_fee_weight as Weights) ?? Weights.none,
    population_center_weight:
      (adsSearchCritera?.population_center_weight as Weights) ?? Weights.none,
    rooms_weight: (adsSearchCritera?.rooms_weight as Weights) ?? Weights.none,
    registered_date_weight:
      (adsSearchCritera?.registered_date_weight as Weights) ?? Weights.none,
  };

  const adsFilterValues = {
    home_type: adsSearchCritera?.home_type?.length
      ? adsSearchCritera.home_type
      : null,
    ownership_type: adsSearchCritera?.ownership_type?.length
      ? adsSearchCritera.ownership_type
      : null,
    /*registered_date_min: adsSearchCritera?.registered_date_min
      ? new Date(adsSearchCritera?.registered_date_min)
      : null,
    registered_date_max: adsSearchCritera?.registered_date_max
      ? new Date(adsSearchCritera?.registered_date_max)
      : null,*/
    living_area_min: adsSearchCritera.living_area_min ?? null,
    living_area_max: adsSearchCritera.living_area_max ?? null,
    rooms_max: adsSearchCritera.rooms_max ?? null,
    rooms_min: adsSearchCritera.rooms_min ?? null,
    price_max:
      adsSearchCritera.price_max && adsSearchCritera?.price_max / 1000000,
    price_min:
      adsSearchCritera.price_min && adsSearchCritera?.price_min / 1000000,
    construction_year_max: adsSearchCritera.construction_year_max ?? null,
    construction_year_min: adsSearchCritera.construction_year_min ?? null,
    ad_advertising_days_max: adsSearchCritera.ad_advertising_days_max ?? null,
    ad_advertising_days_min: adsSearchCritera.ad_advertising_days_min ?? null,
    limit: adsSearchCritera.limit ?? null,
  };

  const cleanAdsFiltedValues = removeNullApiParams(
    adsFilterValues
  ) as Partial<AdsFilterDataType>;

  return {
    adsRefValues,
    adsWeightValues,
    adsFilterValues: cleanAdsFiltedValues,
  };
};

const getSoldFilterParamsFromApiData = (
  salesSearchCritera: SoldSearchCriteriaType
) => {
  const soldRefValues = {
    construction_year_reference:
      salesSearchCritera.construction_year_reference ?? null,
    floor_reference: salesSearchCritera.floor_reference ?? null,
    floors_reference: salesSearchCritera.floors_reference ?? null,
    living_area_reference: salesSearchCritera.living_area_reference ?? null,
    monthly_fee_reference: salesSearchCritera.monthly_fee_reference ?? null,
    rooms_reference: salesSearchCritera.rooms_reference ?? null,
    use_fpr_data: true // Should always be true
  };

  const soldWeightValues = {
    construction_year_weight:
      (salesSearchCritera.construction_year_weight as Weights) ?? Weights.none,
    contract_date_weight:
      (salesSearchCritera.contract_date_weight as Weights) ?? Weights.none,
    distance_weight:
      (salesSearchCritera.distance_weight as Weights) ?? Weights.none,
    floor_weight: (salesSearchCritera?.floor_weight as Weights) ?? Weights.none,
    floors_weight:
      (salesSearchCritera.floors_weight as Weights) ?? Weights.none,
    living_area_weight:
      (salesSearchCritera.living_area_weight as Weights) ?? Weights.none,
    monthly_fee_weight:
      (salesSearchCritera.monthly_fee_weight as Weights) ?? Weights.none,
    population_center_weight:
      (salesSearchCritera.population_center_weight as Weights) ?? Weights.none,
    rooms_weight: (salesSearchCritera?.rooms_weight as Weights) ?? Weights.none,
  };

  const soldFilterValues = {
    home_type: salesSearchCritera?.home_type?.length
      ? salesSearchCritera.home_type
      : null,
    ownership_type: salesSearchCritera?.ownership_type?.length
      ? salesSearchCritera.ownership_type
      : null,
    contract_date_min: salesSearchCritera.contract_date_min
      ? new Date(salesSearchCritera.contract_date_min)
      : null,
    contract_date_max: salesSearchCritera?.contract_date_max
      ? new Date(salesSearchCritera.contract_date_max)
      : null,
    shore_distance_min: salesSearchCritera.shore_distance_min ?? null,
    shore_distance_max: salesSearchCritera.shore_distance_max ?? null,
    living_area_min: salesSearchCritera.living_area_min ?? null,
    living_area_max: salesSearchCritera.living_area_max ?? null,
    rooms_max: salesSearchCritera.rooms_max ?? null,
    rooms_min: salesSearchCritera.rooms_min ?? null,
    error_code: salesSearchCritera.error_code ?? null,
    owner_id: salesSearchCritera.owner_id ?? null,
    is_new_construction: salesSearchCritera.is_new_construction ?? null,
    price_max:
      salesSearchCritera.price_max && salesSearchCritera?.price_max / 1000000,
    price_min:
      salesSearchCritera.price_min && salesSearchCritera?.price_min / 1000000,
    construction_year_max: salesSearchCritera.construction_year_max ?? null,
    construction_year_min: salesSearchCritera.construction_year_min ?? null,
    monthly_fee_max: salesSearchCritera.monthly_fee_max ?? null,
    monthly_fee_min: salesSearchCritera.monthly_fee_min ?? null,
    floor_max: salesSearchCritera.floor_max ?? null,
    floor_min: salesSearchCritera.floor_min ?? null,
    limit: salesSearchCritera.limit ?? null,
  };

  //remove nulls so we can prefill it with defaults
  const cleanSoldFiltedValues = removeNullApiParams(soldFilterValues);

  return {
    soldRefValues,
    soldWeightValues,
    soldFilterValues: cleanSoldFiltedValues,
  };
};

const subtractYearsFromDate = (date: Date, years: number): Date => {
  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() - years);
  return newDate;
};

const getSelectedRefSqmPrice = (
  soldTableData: SalesRefTableType[],
  soldSelectedTableIds: Set<string> | null
) => {
  const tableData = soldTableData;

  let sqmPriceSelected: number | null = null;

  if (soldSelectedTableIds && soldSelectedTableIds.size > 0 && tableData) {
    sqmPriceSelected = 0;
    const selectedItems = tableData.filter((obj) =>
      soldSelectedTableIds.has(obj.id)
    );

    for (let i = 0; i < selectedItems.length; i++) {
      sqmPriceSelected += selectedItems[i].recountedPriceSqm;
    }

    sqmPriceSelected /= soldSelectedTableIds.size;
    sqmPriceSelected = Math.floor(sqmPriceSelected);
  }

  return sqmPriceSelected;
};

const removeStreetNumberFromString = (inputString: string): string => {
  const pattern = /\b\d+[a-zA-Z]*\b/g;
  const result = inputString.replace(pattern, '');

  return result.replace(/\s+/g, ' ').trim();
};

const getHomeTypeLanguageString = (homeType: string) => {
  if (homeType === HomeTypeEnum.apartment.toLowerCase())
    return strings.filter.housingTypes.apartment;
  if (homeType === HomeTypeEnum.house.toLowerCase())
    return strings.filter.housingTypes.house;
  if (homeType === HomeTypeEnum.townhouse.toLowerCase())
    return strings.filter.housingTypes.townhouse;
  if (homeType === HomeTypeEnum.terracedHouse.toLowerCase())
    return strings.filter.housingTypes.terracedHouse;
  if (homeType === HomeTypeEnum.farm.toLowerCase())
    return strings.filter.housingTypes.farm;
  if (homeType === HomeTypeEnum.plot.toLowerCase())
    return strings.filter.housingTypes.plot;
  return '';
};

const getOwnershipLanguageString = (ownerType: string) => {
  if (ownerType === OwnershipType.coOperativeApartment)
    return strings.filter.ownertypes.co_operative;
  if (ownerType === OwnershipType.propertyRights)
    return strings.filter.ownertypes.property_right;
  if (ownerType === OwnershipType.siteLeasehold)
    return strings.filter.ownertypes.site_leasehold;
  if (ownerType === OwnershipType.lease) return strings.filter.ownertypes.lease;
  if (ownerType === OwnershipType.other) return strings.filter.ownertypes.other;
  return '';
};

const firstLetterCaps = (value: string): string => {
  if (!value || value.length < 2) {
    return value;
  }
  return value.substring(0, 1).toUpperCase() + value.substring(1, value.length);
}

const removeNullApiParams = (filterApiParams: any) => {
  return Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(filterApiParams).filter(([key, value]) => value !== null)
  );
};

const getHomeTypeApiParams = (homeType: string[] | null) => {
  return homeType && homeType.length > 0 ? homeType : null;
};

const getCoordinatesLatLng = (
  lat?: number | null,
  lng?: number | null
): CoordinatesType => (lat && lng ? { lat, lng } : defaultCoordinates);


function convertBoundsToSquareSides(bounds): squareSideType {
  if (!bounds || (!bounds.east || !bounds.west || !bounds.east || !bounds.south)) {
    return null;
  }
  const { east, north, south, west } = bounds;
  const cMax = { lat: north, lng: east };
  const cMin = { lat: south, lng: west };
  const transformedCoordsMax = getRT90fromWGS84(cMax.lat, cMax.lng);
  const transformedCoordsMin = getRT90fromWGS84(cMin.lat, cMin.lng);

  const squareSides = {
    rt90x_min: transformedCoordsMin.rt90x,
    rt90y_min: transformedCoordsMin.rt90y,
    rt90x_max: transformedCoordsMax.rt90x,
    rt90y_max: transformedCoordsMax.rt90y,
  }

  return squareSides;
}

function activateSentry() {
  Sentry.init({
    dsn: 'https://33d7cfd0eae3d683a410d00de01123c7@o4508403080953856.ingest.de.sentry.io/4508403083247696',
    integrations: [
      Sentry.browserTracingIntegration({ enableHTTPTimings: true, _experiments: { enableInteractions: true } }),
      Sentry.replayIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.captureConsoleIntegration()
    ],
    beforeSend(event) {
      if (!isLoggedIn()) {
        //window.location.href = PATH.login;
      }
      if (import.meta.env.VITE_ENVIRONMENT === "dev" || window.location.hostname === 'localhost') {
        return null;
      }
      return event;
    },
    environment: import.meta.env.VITE_ENVIRONMENT,
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/analytics-dev\.valueguard\.se/,
      /^https:\/\/analytics-prod\.valueguard\.se/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const throttle = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T => {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastExecTime = 0;

  return ((...args: Parameters<T>) => {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > delay) {
      func(...args);
      lastExecTime = currentTime;
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  }) as T;
};

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  let osName = 'Unknown';
  let deviceType = 'Desktop';

  // Detect browser
  if (ua.indexOf('Firefox') > -1) {
    browserName = 'Firefox';
    browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Edg') > -1) {
    browserName = 'Edge';
    browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Chrome') > -1) {
    browserName = 'Chrome';
    browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Safari') > -1) {
    browserName = 'Safari';
    browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
  }

  // Detect OS
  if (ua.indexOf('Windows') > -1) {
    osName = 'Windows';
  } else if (ua.indexOf('Mac') > -1) {
    osName = 'macOS';
  } else if (ua.indexOf('Linux') > -1) {
    osName = 'Linux';
  } else if (ua.indexOf('Android') > -1) {
    osName = 'Android';
    deviceType = 'Mobile';
  } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
    osName = 'iOS';
    deviceType = ua.indexOf('iPad') > -1 ? 'Tablet' : 'Mobile';
  }

  // Detect device type for desktop
  if (deviceType === 'Desktop' && 'ontouchstart' in window) {
    deviceType = 'Tablet';
  }

  return {
    browser: `${browserName} ${browserVersion}`,
    browserName,
    browserVersion,
    os: osName,
    deviceType,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
};

const trackUserLogin = (userId: string, email: string) => {
  const deviceInfo = getBrowserInfo();

  // Set user in Sentry
  Sentry.setUser({
    id: userId,
  });

  // Track login with device information
  Sentry.captureMessage('user_session_started', {
    level: 'info',
    tags: {
      event_type: 'user_session',
      browser: deviceInfo.browserName,
      browser_version: deviceInfo.browserVersion,
      os: deviceInfo.os,
      device_type: deviceInfo.deviceType,
    },
    contexts: {
      browser: {
        name: deviceInfo.browserName,
        version: deviceInfo.browserVersion,
      },
      os: {
        name: deviceInfo.os,
      },
      device: {
        type: deviceInfo.deviceType,
      },
    },
    extra: {
      screen_resolution: deviceInfo.screenResolution,
      viewport: deviceInfo.viewport,
    },
  });
};

export {
  activateSentry,
  getDistance,
  calculateBounds,
  getAboutListItems,
  removeNullApiParams,
  getSalesReferenceData,
  getTodayDateISO,
  getAdsData,
  getCoordinatesLatLng,
  getHomeTypeLanguageString,
  getHomeTypeApiParams,
  getOwnershipLanguageString,
  getMapMarkerDataFromAds,
  getMapMarkerDataFromSold,
  getMapMarkerDataFromAdsTable,
  getMapMarkerDataFromSoldTable,
  getMarkerLabel,
  getWGS84fromRT90,
  getRT90fromWGS84,
  modifyValueBasedOnKeyForTable,
  modifyValueBasedOnKeyForExcel,
  getRefApiParams,
  getSelectedRefSqmPrice,
  getWeightApiParams,
  getMarketsFilterParamsFromApiData,
  getAdsFilterParamsFromApiData,
  getSoldFilterParamsFromApiData,
  convertBoundsToSquareSides,
  formatPrice,
  formatNumber,
  removeStreetNumberFromString,
  convertDateStringToISO,
  formatDateToString,
  subtractYearsFromDate,
  isLoggedIn,
  convertDateToISO,
  getCookie,
  firstLetterCaps,
  filterLineChartDataBasedOnDateInterval,
  isContrastWCAG,
  throttle,
  getBrowserInfo,
  trackUserLogin
};
